/**
 * ShapeSpawner — Creates and animates VFX shape primitives.
 * Extracted from VFXEffectPlayer.
 *
 * Phase H10 Arc λ.12 (2026-05-11): All primitives now render through
 * substrate-agnostic IGraphics (createGraphics). Removed Phaser shape
 * pools (Arc / Rectangle / Ellipse) and raw scene.add.star/polygon
 * calls. PixiGraphics implements IShape-compatible properties so
 * TweenHost can animate x/y/alpha/scale/rotation directly.
 */

import { VFXPrimitive } from '../VFXEffectRegistry';
import { facingOffsetX } from './facingOffsetX';
import { gameLogger } from '../../../utils/GameLogger';
import { vfxBudgetManager } from '../VFXBudgetManager';
import type { IShape, IGraphics, GraphicsStyle } from '../../rendering/IRenderer';
import { TweenHost, mapEase, type TweenHostHandle } from '../../rendering/TweenHost';
import { createGraphics } from '../../rendering/utils/Graphics';
import { DisplayPool, isParkable, type Parkable } from '../../rendering/pool/DisplayPool';
import { shapePoolKey } from './shapePoolKey';

type PooledShape = IShape & Parkable;

/** Types whose graphics alpha is set from the primitive (the rest bake it into the fill). */
const GFX_ALPHA_TYPES = new Set(['arc', 'cone', 'beam', 'shockwave', 'line']);

export type DestroyCallback = (obj: IShape) => void;

export class ShapeSpawner {
  /**
   * Every shape ShapeSpawner creates, until it's released. createShape is the
   * universal chokepoint (spawnWithTween / spawnScattered / persistent all
   * route through it), so this captures fire-and-forget transients — scatter
   * snow, falling particles — that no orbiter/follower/persistent tracker
   * owns. The capture render-truth summary reads these by position (it could
   * only see a coarse budget count before). Tracker-owned shapes are deduped
   * at read time by object identity, so a tracked shape is never double-counted.
   */
  private live = new Set<IShape>();
  /**
   * Released shapes parked for reuse (plan 16 §2.2). A shape stays attached
   * to its layer at its depth; a cast re-acquires it by key, so a fight stops
   * adding and removing a dozen Graphics per cast (each a stage rebuild).
   */
  private pool = new DisplayPool<PooledShape>({ perKeyCap: 4, globalCap: 96 });
  private poolKeys = new WeakMap<IShape, string>();
  /** The tween ShapeSpawner started on a shape — killed when it is released. */
  private tweens = new Map<IShape, TweenHostHandle>();
  /** End-of-effect fades — killed on release too, or they re-show a parked shape. */
  private fades = new Map<IShape, TweenHostHandle>();

  constructor(private scene: unknown) {}

  /** Live shapes (still active) — for the capture render-truth summary. */
  public getLiveShapes(): IShape[] {
    const out: IShape[] = [];
    this.live.forEach((o) => {
      if ((o as { active?: boolean }).active === true) out.push(o);
    });
    return out;
  }

  public setScene(scene: unknown): void {
    this.scene = scene;
  }

  /** Create a VFX shape from a VFXPrimitive config. */
  public createShape(
    prim: VFXPrimitive,
    x: number,
    y: number,
    overrides?: { color?: number; scale?: number; flipX?: boolean },
  ): IShape | null {
    const isGraphicsType = prim.type === 'arc' || prim.type === 'beam'
      || prim.type === 'shockwave' || prim.type === 'line'
      || prim.type === 'star' || prim.type === 'polygon'
      || prim.type === 'circle' || prim.type === 'rect'
      || prim.type === 'ellipse' || prim.type === 'cone';

    // Budget check: skip if over budget
    if (isGraphicsType && !vfxBudgetManager.canSpawnGraphics()) {
      gameLogger.debug('vfx', `[ShapeSpawner] Graphics budget exceeded, skipping ${prim.type}`);
      return null;
    }
    if (!vfxBudgetManager.canSpawnShape()) {
      gameLogger.debug('vfx', `[ShapeSpawner] Shape budget exceeded, skipping ${prim.type}`);
      return null;
    }

    const color = overrides?.color ?? prim.color ?? 0xffffff;
    const alpha = prim.alpha ?? 1;
    const depth = prim.depth ?? 90;
    // Glow primitives route into the bloom layer (additive + blur); all
    // graphics also carry their depth so the glow/flat ordering is stable.
    const gfxStyle: GraphicsStyle = prim.glow ? { depth, glow: true } : { depth };
    const { key, rng } = shapePoolKey(prim, color, overrides?.flipX === true, depth);

    let obj: IShape | null = this.pool.take(key);
    if (obj) {
      // Same key = same layer, depth and drawing: only the transform is new.
      obj.setPosition(x, y);
      obj.setAlpha(GFX_ALPHA_TYPES.has(prim.type) && alpha < 1 ? alpha : 1);
    } else {
      obj = this.drawShape(prim, x, y, color, alpha, gfxStyle, overrides?.flipX === true, rng);
      if (obj && isParkable(obj)) {
        this.pool.noteMade();
        this.poolKeys.set(obj, key);
      }
    }

    if (!obj) return null;
    obj.setDepth(depth);
    if (overrides?.scale) obj.setScale(overrides.scale);
    if (prim.stroke) {
      (obj as any).setStrokeStyle?.(prim.stroke.width, prim.stroke.color);
    }
    vfxBudgetManager.onShapeCreated(isGraphicsType);
    this.live.add(obj);
    return obj;
  }

  /** Draw a new graphics for `prim` at the local origin, positioned at (x, y). */
  private drawShape(
    prim: VFXPrimitive,
    x: number,
    y: number,
    color: number,
    alpha: number,
    gfxStyle: GraphicsStyle,
    flipX: boolean,
    rng: () => number,
  ): IShape | null {
    if (prim.type === 'polygon' && !(prim.polygonPoints && prim.polygonPoints.length > 0)) return null;
    let obj: IShape | null = null;
    const gfx: IGraphics = createGraphics(this.scene, gfxStyle);

    switch (prim.type) {
      case 'circle': {
        gfx.fillCircle(0, 0, prim.radius ?? 10, color, alpha);
        gfx.setPosition(x, y);
        gfx.setVisible(true);
        obj = gfx as unknown as IShape;
        break;
      }
      case 'rect': {
        const w = prim.width ?? 20;
        const h = prim.height ?? 20;
        gfx.fillRect(-w / 2, -h / 2, w, h, color, alpha);
        gfx.setPosition(x, y);
        gfx.setVisible(true);
        obj = gfx as unknown as IShape;
        break;
      }
      case 'ellipse': {
        gfx.fillEllipse(0, 0, prim.width ?? 30, prim.height ?? 20, color, alpha);
        gfx.setPosition(x, y);
        gfx.setVisible(true);
        obj = gfx as unknown as IShape;
        break;
      }
      case 'star': {
        const points = prim.points ?? 5;
        const innerR = prim.innerRadius ?? 8;
        const outerR = prim.outerRadius ?? 16;
        const starPoints: Array<{ x: number; y: number }> = [];
        for (let i = 0; i < points * 2; i++) {
          const angle = (i * Math.PI) / points - Math.PI / 2;
          const r = i % 2 === 0 ? outerR : innerR;
          starPoints.push({ x: Math.cos(angle) * r, y: Math.sin(angle) * r });
        }
        gfx.fillPolygon(starPoints, color, alpha);
        gfx.setPosition(x, y);
        gfx.setVisible(true);
        obj = gfx as unknown as IShape;
        break;
      }
      case 'polygon':
        if (prim.polygonPoints && prim.polygonPoints.length > 0) {
            const pts: Array<{ x: number; y: number }> = [];
          const flat = prim.polygonPoints;
          // flipX mirrors asymmetric silhouettes for left-facing casts —
          // arc/cone/beam/line already mirror; without this, ghostly_strike's
          // blade polygon (tip at +42) pointed right while its arc swept left.
          const flipSign = flipX ? -1 : 1;
          for (let i = 0; i < flat.length; i += 2) {
            pts.push({ x: flat[i] * flipSign, y: flat[i + 1] });
          }
          gfx.fillPolygon(pts, color, alpha);
          gfx.setPosition(x, y);
          gfx.setVisible(true);
          obj = gfx as unknown as IShape;
        }
        break;
      case 'arc': {
        const arcR = prim.arcRadius ?? prim.radius ?? 40;
        // arcStartAngle/arcEndAngle are authored in DEGREES, right-facing
        // basis (the only def, sword_swing, was always degrees — feeding
        // them raw into strokeArc's radians made a near-full wrap that read
        // as a giant wrong-way sweep). flipX mirrors across the vertical
        // axis for left-facing casts: θ → 180° − θ with start/end swapped.
        const startDeg = prim.arcStartAngle ?? -90;
        const endDeg = prim.arcEndAngle ?? 90;
        const flip = flipX;
        const fromDeg = flip ? 180 - endDeg : startDeg;
        const toDeg = flip ? 180 - startDeg : endDeg;
        // Draw relative to origin (0,0) then position — same convention as
        // circle/rect/ellipse. Drawing at absolute (x,y) here would compound
        // with the graphic's own position (and any x/y tween), landing the
        // effect far off-screen.
        gfx.strokeArc(0, 0, arcR, fromDeg * (Math.PI / 180), toDeg * (Math.PI / 180), color ?? 0xffffff, prim.stroke?.width ?? 4, alpha);
        gfx.setPosition(x, y);
        if (alpha < 1) gfx.setAlpha(alpha);
        obj = gfx as unknown as IShape;
        break;
      }
      case 'cone': {
        // A FILLED angular sector (wedge) — the thing `arc` (a stroke) can't draw:
        // breath attacks, frost cones, directional novas. Built as a fan polygon
        // (apex at the origin + points sampled along the arc), filled. Same
        // DEGREES / right-facing basis + flipX mirroring as `arc`; drawn at the
        // local origin then positioned (the shared coordinate convention).
        const coneR = prim.coneRadius ?? prim.radius ?? 60;
        const startDeg = prim.coneStartAngle ?? -30;
        const endDeg = prim.coneEndAngle ?? 30;
        const flip = flipX;
        const fromDeg = flip ? 180 - endDeg : startDeg;
        const toDeg = flip ? 180 - startDeg : endDeg;
        const fromRad = fromDeg * (Math.PI / 180);
        const toRad = toDeg * (Math.PI / 180);
        // One arc point per ~10° so even a wide cone stays smooth, min 2.
        const segs = Math.max(2, Math.ceil(Math.abs(toDeg - fromDeg) / 10));
        const pts: Array<{ x: number; y: number }> = [{ x: 0, y: 0 }];
        for (let i = 0; i <= segs; i += 1) {
          const a = fromRad + (toRad - fromRad) * (i / segs);
          pts.push({ x: Math.cos(a) * coneR, y: Math.sin(a) * coneR });
        }
        gfx.fillPolygon(pts, color ?? 0xffffff, alpha);
        gfx.setPosition(x, y);
        if (alpha < 1) gfx.setAlpha(alpha);
        obj = gfx as unknown as IShape;
        break;
      }
      case 'beam': {
        const length = prim.beamLength ?? 100;
        const bw = prim.beamWidth ?? 4;
        const segments = prim.beamSegments ?? 1;
        const jitter = prim.lineJitter ?? bw * 2;

        // Build the path relative to origin (0,0) then position the graphic
        // at (x,y) — same convention as the other primitives. Drawing at
        // absolute coords would compound with the graphic's position (and
        // any x/y tween), landing the beam far off-screen.
        const direction = flipX ? -1 : 1;
        const points: Array<{ x: number; y: number }> = [{ x: 0, y: 0 }];
        if (segments > 1) {
          const segLen = length / segments;
          for (let i = 1; i < segments; i++) {
            points.push({
              x: direction * segLen * i,
              y: (rng() - 0.5) * jitter,
            });
          }
        }
        points.push({ x: direction * length, y: 0 });

        // Three-layer beam render: glow (wider/softer), core, bright center.
        const beamColor = color ?? 0xffffff;
        gfx.strokePolyline(points, beamColor, bw * 3, alpha * 0.25);
        gfx.strokePolyline(points, beamColor, bw, alpha);
        gfx.strokePolyline(points, 0xffffff, Math.max(1, bw * 0.4), alpha * 0.8);
        gfx.setPosition(x, y);

        if (alpha < 1) gfx.setAlpha(alpha);
        obj = gfx as unknown as IShape;
        break;
      }
      case 'shockwave': {
        const inner = prim.shockwaveInnerRadius ?? 20;
        const outer = prim.shockwaveOuterRadius ?? 40;
        const swColor = color ?? 0xffffff;
        const lw = prim.stroke?.width ?? 2;
        // Outer ring filled with translucent color (hollow center via 0
        // alpha inner fill in original; with IGraphics-driven render we
        // skip that — visual difference is negligible since both rings
        // are stroked over). Draw rings centered on origin (0,0) then
        // position — same convention as circle/rect/ellipse. Drawing at
        // absolute (x,y) would compound with the graphic's position (and
        // any x/y tween), landing the effect far off-screen.
        gfx.fillCircle(0, 0, outer, swColor, alpha * 0.3);
        gfx.strokeCircle(0, 0, outer, swColor, lw, alpha);
        gfx.strokeCircle(0, 0, inner, swColor, lw, alpha);
        gfx.setPosition(x, y);
        if (alpha < 1) gfx.setAlpha(alpha);
        obj = gfx as unknown as IShape;
        break;
      }
      case 'line': {
        const lw = prim.lineWidth ?? 2;
        const jitter = prim.lineJitter ?? 0;
        if (prim.linePoints && prim.linePoints.length > 0) {
          // Build the path relative to origin (0,0) then position the
          // graphic at (x,y) — same convention as the other primitives.
          // Drawing at absolute coords would compound with the graphic's
          // position (and any x/y tween), landing the line far off-screen.
          const direction = flipX ? -1 : 1;
          const first = prim.linePoints[0];
          const points: Array<{ x: number; y: number }> = [{ x: direction * first.x, y: first.y }];
          for (let i = 1; i < prim.linePoints.length; i++) {
            const pt = prim.linePoints[i];
            const jx = jitter > 0 ? (rng() - 0.5) * jitter : 0;
            const jy = jitter > 0 ? (rng() - 0.5) * jitter : 0;
            points.push({ x: direction * (pt.x + jx), y: pt.y + jy });
          }
          gfx.strokePolyline(points, color ?? 0xffffff, lw, alpha);
          gfx.setPosition(x, y);
        }
        if (alpha < 1) gfx.setAlpha(alpha);
        obj = gfx as unknown as IShape;
        break;
      }
      default:
        gameLogger.warn('vfx', `[ShapeSpawner] Unknown primitive type: ${prim.type}`);
        gfx.destroy();
        return null;
    }
    return obj;
  }

  /**
   * Release a shape: park it for reuse (or destroy it when its pool key is
   * full, it is not poolable, or `destroy` is set). Idempotent — the tween's
   * release wrapper and VFXEffectPlayer.destroyObject both release the same
   * shape, and a second park would hand one object to two owners.
   */
  public releaseShape(obj: IShape, destroy = false): void {
    if (!this.live.delete(obj)) {
      // Already released (parked objects are left alone), or never ours:
      // a shape handed in from elsewhere is destroyed as before.
      if (!this.poolKeys.has(obj)) obj.destroy();
      return;
    }
    this.tweens.get(obj)?.kill(); this.tweens.delete(obj);
    this.fades.get(obj)?.kill(); this.fades.delete(obj);
    vfxBudgetManager.onShapeDestroyed(true);
    const key = this.poolKeys.get(obj);
    if (!destroy && key !== undefined && isParkable(obj)) {
      this.pool.release(key, obj as PooledShape);
    } else {
      obj.destroy();
    }
  }

  /** Pool counters (live/parked/made/reused/destroyed) for diagnostics + tests. */
  public getPoolStats() {
    return this.pool.stats();
  }

  /**
   * K24 (2026-07-17): release every live shape not in `preserve`. Catches
   * fire-and-forget transients (in-flight bursts) AND repeat:-1 hand-rolls
   * (the ice_wall class — their tween onComplete never fires, so nothing
   * else ever releases them). Returns the number of shapes released.
   */
  public sweepLive(preserve?: ReadonlySet<IShape>): number {
    let released = 0;
    for (const obj of Array.from(this.live)) {
      if (preserve?.has(obj)) continue;
      // Destroy, not park: trackers prune released shapes by `!active` on
      // their next tick, and a new map starts with a fresh pool.
      this.releaseShape(obj, true);
      released++;
    }
    this.pool.drain();
    return released;
  }

  /** Fade a live shape to alpha 0 over `ms`, then hand it to `onDone`. */
  public fadeOut(obj: IShape, ms: number, onDone: DestroyCallback): void {
    this.fades.set(obj, TweenHost.add(obj, { to: { alpha: 0 }, durationMs: ms, onComplete: () => onDone(obj) }));
  }

  /** Spawn a shape and apply its tween animation. Returns the shape or null. */
  public spawnWithTween(
    prim: VFXPrimitive,
    x: number,
    y: number,
    overrides?: { color?: number; scale?: number; duration?: number; flipX?: boolean },
    onDestroy?: DestroyCallback,
  ): IShape | null {
    const px = x + facingOffsetX(prim, overrides);
    const py = y + (prim.offsetY || 0);
    const obj = this.createShape(prim, px, py, overrides);
    if (!obj) return null;

    const releaseWrapper = (o: IShape) => { this.releaseShape(o); onDestroy?.(o); };

    if (!prim.tween) return obj;

    this.applyTween(obj, prim, px, py, overrides, releaseWrapper);
    return obj;
  }

  /** Spawn scattered copies in a radial ring pattern. */
  public spawnScattered(
    prim: VFXPrimitive,
    originX: number,
    originY: number,
    overrides?: { color?: number; scale?: number; duration?: number; flipX?: boolean },
    onDestroy?: DestroyCallback,
  ): IShape[] {
    const count = prim.scatter!.count;
    const radius = prim.scatter!.radius;
    const spawned: IShape[] = [];

    const releaseWrapper = (o: IShape) => { this.releaseShape(o); onDestroy?.(o); };

    // Mirrored spawn offset (B22); the tween target stays the un-offset ring (RangerVFX shafts).
    const offsetX = facingOffsetX(prim, overrides);
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const spawnX = originX + Math.cos(angle) * (radius * 0.3);
      const spawnY = originY + Math.sin(angle) * (radius * 0.3);

      const obj = this.createShape(prim, spawnX + offsetX, spawnY + (prim.offsetY || 0), overrides);
      if (!obj) continue;
      spawned.push(obj);

      if (!prim.tween) continue;

      const repeat = prim.tween.repeat ?? 0;
      const to: Record<string, number> = {
        x: originX + Math.cos(angle) * radius,
        y: originY + Math.sin(angle) * radius,
        alpha: prim.tween.alpha ?? 0,
      };
      if (prim.tween.scale !== undefined) {
        const s = (overrides?.scale ?? 1) * prim.tween.scale;
        // Phaser's `scale: N` is shorthand for `scaleX = scaleY = N`.
        to.scaleX = s;
        to.scaleY = s;
      }
      this.tweens.set(obj, TweenHost.addDelayed(obj, prim.tween.delay ?? 0, {
        to,
        durationMs: overrides?.duration ?? prim.tween.duration,
        ease: mapEase(prim.tween.ease || 'Power2'),
        yoyo: prim.tween.yoyo || false,
        repeat,
        onComplete: () => {
          if (repeat !== -1) releaseWrapper(obj);
        },
      }));
    }

    return spawned;
  }

  private applyTween(
    obj: IShape,
    prim: VFXPrimitive,
    x: number,
    y: number,
    overrides?: { color?: number; scale?: number; duration?: number; flipX?: boolean },
    onDestroy?: DestroyCallback,
  ): void {
    const tween = prim.tween!;
    const repeat = tween.repeat ?? 0;
    const to: Record<string, number> = {};

    if (tween.alpha !== undefined) to.alpha = tween.alpha;
    if (tween.rotation !== undefined) to.rotation = tween.rotation;
    // tween.x is authored right-facing; mirror the travel with the cast.
    if (tween.x !== undefined) to.x = x + (overrides?.flipX === true ? -tween.x : tween.x);
    if (tween.y !== undefined) to.y = y + tween.y;
    if (tween.scale !== undefined) {
      const s = (overrides?.scale ?? 1) * tween.scale;
      to.scaleX = s;
      to.scaleY = s;
    }
    if (tween.scaleX !== undefined) to.scaleX = (overrides?.scale ?? 1) * tween.scaleX;
    if (tween.scaleY !== undefined) to.scaleY = (overrides?.scale ?? 1) * tween.scaleY;

    this.tweens.set(obj, TweenHost.addDelayed(obj, tween.delay ?? 0, {
      to,
      durationMs: overrides?.duration ?? tween.duration,
      ease: mapEase(tween.ease || 'Power2'),
      yoyo: tween.yoyo || false,
      repeat,
      onComplete: () => {
        if (repeat !== -1) onDestroy?.(obj);
      },
    }));
  }

  /** Get active stats for diagnostics overlay */
  public getActiveStats() {
    return vfxBudgetManager.getStats();
  }
}
